// Pieces shared by the Web payments page: the switch, the error box with plain copy, the Stripe dashboard link,
// status pills for transactions and webhook events, and a draft that follows the server value.

import { useState, type ReactNode } from "react";
import { ApiError } from "../../api/client";
import type { Transaction } from "../../api/paymentsSchemas";
import { Chip } from "../../components/ui";
import { paymentsErrorCopy } from "../../lib/payments";
import pages from "../pages.module.css";
import p from "./payments.module.css";

export function Switch({ checked, label, disabled, onClick, testId }: { checked: boolean; label: string; disabled?: boolean; onClick: () => void; testId: string }) {
  return (
    <button type="button" role="switch" className={p.switch} aria-checked={checked} aria-label={label} disabled={disabled} onClick={onClick} data-testid={testId}>
      <span className={p.switchKnob} />
    </button>
  );
}

export function ReadOnly({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <span className={pages.readOnly} data-testid={testId}>
      {children}
    </span>
  );
}

/** An API error in plain words, with the server's code and message underneath. */
export function PaymentsError({ error, context, testId = "payments-error" }: { error: unknown; context?: "keys" | "settings" | "package"; testId?: string }) {
  if (!error) return null;
  const copy = paymentsErrorCopy(error, context);
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className={p.error} role="alert" data-testid={testId}>
      <span>{copy ?? message}</span>
      {copy && error instanceof ApiError ? (
        <span className={p.errorDetail}>
          <code>{error.code}</code>
          {message}
        </span>
      ) : null}
    </div>
  );
}

/** A Stripe dashboard link; always a new tab. */
export function StripeLink({ href, children = "Open in Stripe" }: { href: string | null | undefined; children?: ReactNode }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={p.stripeLink}>
      {children}
      <span aria-hidden>↗</span>
      <span className="visually-hidden"> (opens in a new tab)</span>
    </a>
  );
}

const TX_STATUS: Record<string, { label: string; tone: string }> = {
  active: { label: "active", tone: "success" },
  trialing: { label: "trial", tone: "violet" },
  past_due: { label: "past due", tone: "warning" },
  unpaid: { label: "unpaid", tone: "danger" },
  incomplete: { label: "incomplete", tone: "warning" },
  incomplete_expired: { label: "expired", tone: "muted" },
  canceled: { label: "canceled", tone: "muted" },
  expired: { label: "ended", tone: "muted" },
  paid: { label: "paid", tone: "success" },
  refunded: { label: "refunded", tone: "danger" },
  disputed: { label: "disputed", tone: "danger" },
  revoked: { label: "revoked", tone: "danger" },
};

/** The status the API sent, else one derived from the row. */
export function txStatus(t: Pick<Transaction, "status" | "revokedAt" | "inTrial" | "expiresAt">): string {
  if (t.status) return t.status;
  if (t.revokedAt) return "revoked";
  if (t.inTrial) return "trialing";
  if (t.expiresAt === null) return "paid";
  return Date.parse(t.expiresAt) > Date.now() ? "active" : "expired";
}

export function TxStatusPill({ status }: { status: string }) {
  const s = TX_STATUS[status] ?? { label: status.replace(/_/g, " "), tone: "neutral" };
  return (
    <Chip tone={s.tone} title={status}>
      {s.label}
    </Chip>
  );
}

const EVENT_RESULT: Record<string, { label: string; tone: string }> = {
  processed: { label: "processed", tone: "success" },
  ok: { label: "processed", tone: "success" },
  applied: { label: "processed", tone: "success" },
  ignored: { label: "ignored", tone: "muted" },
  duplicate: { label: "duplicate", tone: "muted" },
  orphan: { label: "orphan", tone: "warning" },
  error: { label: "error", tone: "danger" },
  failed: { label: "error", tone: "danger" },
};

export function EventResultPill({ result }: { result: string | null }) {
  const r = result ? (EVENT_RESULT[result] ?? { label: result, tone: "neutral" }) : { label: "pending", tone: "info" };
  return (
    <Chip tone={r.tone} title={result ?? "not processed yet"}>
      {r.label}
    </Chip>
  );
}

const same = (x: unknown, y: unknown) => JSON.stringify(x) === JSON.stringify(y);

/** A draft that follows the server value until it's edited (and again after a save). */
export function useDraft<T>(server: T) {
  const [draft, setDraft] = useState(server);
  const [base, setBase] = useState(JSON.stringify(server));
  if (base !== JSON.stringify(server)) {
    setBase(JSON.stringify(server));
    setDraft(server);
  }
  return [draft, setDraft, !same(draft, server)] as const;
}
